import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { squidloansUserAPI } from '../../services/api';
import FlowFooter from '../../components/FlowFooter';
import useLeadStage from "../../hooks/useLeadStage";
import { automationNodeLeadStageToRouteMap, LEAD_STAGE } from "../../Utils/constants";

const curr_page_lead_stage = [
  LEAD_STAGE.COMPLETE_REGISTRATION,
];

function Employment() {
  const navigate = useNavigate();
  const { leadStage, isLoadingStage } = useLeadStage();
  const [isLoading, setIsLoading] = useState(false);
  const [workStatus, setWorkStatus] = useState('');
  const [formData, setFormData] = useState({
    companyName: '',
    salaryDate: '',
    monthlySalary: '',
    pincode: '',
  });
  const [errors, setErrors] = useState({
    workStatus: '',
    companyName: '',
    salaryDate: '',
    monthlySalary: '',
    pincode: '',
  });
  const [userLocation, setUserLocation] = useState({ lat: null, lng: null });
  const [isLocationLoading, setIsLocationLoading] = useState(true);
  const [apiError, setApiError] = useState('');

  // Check routing after journey is loaded
  useEffect(() => {
    if (isLoadingStage) {
      return;
    }

    if (leadStage && !curr_page_lead_stage.includes(leadStage)) {
      navigate(automationNodeLeadStageToRouteMap[leadStage]);
    }
  }, [leadStage, navigate, isLoadingStage]);
  
  useEffect(() => {
    if (!navigator.geolocation) {
      setIsLocationLoading(false);
      return;
    }
    let isMounted = true;
    setIsLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (isMounted) {
          setUserLocation({
            lat: pos.coords.latitude.toString(),
            lng: pos.coords.longitude.toString(),
          });
          setIsLocationLoading(false);
        }
      },
      () => {
        if (isMounted) setIsLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
    return () => { isMounted = false; };
  }, []);

  const handleWorkStatusChange = (e) => {
    setWorkStatus(e.target.value);
    setErrors((prev) => ({ ...prev, workStatus: '' }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    if (name === 'pincode') {
      processedValue = value.replace(/\D/g, '').slice(0, 6);
    } else if (name === 'monthlySalary') {
      processedValue = value.replace(/\D/g, '').slice(0, 7);
    } else if (name === 'companyName') {
      processedValue = value.replace(/[^a-zA-Z0-9\s'&.-]/g, '');
    }
    setFormData((prev) => ({ ...prev, [name]: processedValue }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    setApiError('');
  };

  const validateForm = () => {
    const newErrors = {};
    if (!workStatus) newErrors.workStatus = 'Please select your work status';
    if (workStatus === 'salaried') {
      if (!formData.companyName) newErrors.companyName = 'Company name is required';
      if (!formData.salaryDate) newErrors.salaryDate = 'Salary date is required';
      if (!formData.monthlySalary) {
        newErrors.monthlySalary = 'Monthly salary is required';
      } else if (isNaN(formData.monthlySalary) || parseInt(formData.monthlySalary, 10) <= 0) {
        newErrors.monthlySalary = 'Please enter a valid salary amount';
      } else if (parseInt(formData.monthlySalary, 10) > 10000000) {
        newErrors.monthlySalary = 'Monthly salary cannot be greater than 1 crore';
      }
    }
    const pincodeRegex = /^[0-9]{6}$/;
    if (workStatus === 'salaried' && (!userLocation.lat || !userLocation.lng) && !isLocationLoading) {
      if (!formData.pincode) {
        newErrors.pincode = 'Pincode is required when location is not available';
      } else if (!pincodeRegex.test(formData.pincode.trim())) {
        newErrors.pincode = 'Please enter a valid 6-digit pincode';
      }
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    const formErrors = validateForm();
    setErrors(formErrors);
    if (Object.keys(formErrors).length > 0) return;

    setIsLoading(true);
    try {
      const baseData = {
        employee_type: workStatus.toUpperCase(),
        lat: userLocation.lat && userLocation.lng ? userLocation.lat : null,
        lng: userLocation.lat && userLocation.lng ? userLocation.lng : null,
        pincode:
          workStatus === 'salaried' && (!userLocation.lat || !userLocation.lng)
            ? formData.pincode.trim()
            : null,
      };

      if (workStatus === 'self_employed') {
        await squidloansUserAPI.addEmployment(baseData);
        navigate('/apply/thank-you');
      } else {
        await squidloansUserAPI.addEmployment({
          ...baseData,
          company_name: formData.companyName,
          salary_date: formData.salaryDate,
          monthly_salary: formData.monthlySalary,
        });
        navigate('/apply/email');
      }
    } catch (error) {
      setIsLoading(false);
      setApiError(
        error.message ||
          'Failed to submit employment status. Please try again.'
      );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 pt-20 sm:pt-24 md:pt-28">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-md w-full text-center shadow-xl">
            <Loader2 className="h-12 w-12 animate-spin text-[#FF6B35] mx-auto mb-4" />
            <h2 className="text-lg sm:text-xl font-bold text-[#1E3A8A] mb-2">Do not refresh the page</h2>
            <p className="text-sm sm:text-base text-[#64748B]">Your employment information is being processed...</p>
          </div>
        </div>
        <FlowFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>

      <div className="relative flex-1 flex flex-col pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 mt-0 overflow-y-auto">
        <div className="max-w-2xl w-full mx-auto space-y-4 sm:space-y-6 py-2 sm:py-4">
          <div className="text-center space-y-2 sm:space-y-3 mb-2 sm:mb-4 px-2">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold leading-tight text-[#1E3A8A]">
              What is your <span className="text-[#FF6B35]">employment status</span>?
            </h1>
            <p className="text-sm sm:text-base text-[#64748B]">Select your employment type to proceed</p>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-[#FF6B35]/10">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label className="text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-1 block">
                  Select your status <span className="text-[#FF6B35]">*</span>
                </label>
                <select
                  value={workStatus}
                  onChange={handleWorkStatusChange}
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl bg-white border text-sm sm:text-base ${
                    errors.workStatus ? 'border-red-500' : 'border-[#E2E8F0] focus:border-[#FF6B35]'
                  } text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40`}
                >
                  <option value="">Select</option>
                  <option value="salaried">Salaried</option>
                  <option value="self_employed">Self-Employed</option>
                </select>
                {errors.workStatus && <p className="text-xs text-red-500 mt-1 sm:mt-2">{errors.workStatus}</p>}
              </div>

              {workStatus === 'salaried' && (
                <>
                  <div>
                    <label className="text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-1 block">
                      Company Name <span className="text-[#FF6B35]">*</span>
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      placeholder="Company Name"
                      className={`w-full px-3 sm:px-4 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl bg-white border text-sm sm:text-base ${
                        errors.companyName ? 'border-red-500' : 'border-[#E2E8F0] focus:border-[#FF6B35]'
                      } text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40`}
                    />
                    {errors.companyName && <p className="text-xs text-red-500 mt-1 sm:mt-2">{errors.companyName}</p>}
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-1 block">
                      Previous Salary Date <span className="text-[#FF6B35]">*</span>
                    </label>
                    <input
                      type="date"
                      name="salaryDate"
                      value={formData.salaryDate}
                      onChange={handleInputChange}
                      max={new Date().toISOString().split('T')[0]}
                      className={`w-full px-3 sm:px-4 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl bg-white border text-sm sm:text-base ${
                        errors.salaryDate ? 'border-red-500' : 'border-[#E2E8F0] focus:border-[#FF6B35]'
                      } text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40`}
                    />
                    {errors.salaryDate && <p className="text-xs text-red-500 mt-1 sm:mt-2">{errors.salaryDate}</p>}
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-1 block">
                      Monthly Salary <span className="text-[#FF6B35]">*</span>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      name="monthlySalary"
                      value={formData.monthlySalary}
                      onChange={handleInputChange}
                      placeholder="Monthly Salary"
                      className={`w-full px-3 sm:px-4 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl bg-white border text-sm sm:text-base ${
                        errors.monthlySalary ? 'border-red-500' : 'border-[#E2E8F0] focus:border-[#FF6B35]'
                      } text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40`}
                    />
                    {errors.monthlySalary && <p className="text-xs text-red-500 mt-1 sm:mt-2">{errors.monthlySalary}</p>}
                  </div>
                </>
              )}

              {workStatus === 'salaried' &&
                (!userLocation.lat || !userLocation.lng) &&
                !isLocationLoading && (
                  <div>
                    <label className="text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-1 block">
                      Pincode <span className="text-[#FF6B35]">*</span>
                    </label>
                    <input
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      placeholder="6-digit pincode"
                      maxLength={6}
                      className={`w-full px-3 sm:px-4 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl bg-white border text-sm sm:text-base ${
                        errors.pincode ? 'border-red-500' : 'border-[#E2E8F0] focus:border-[#FF6B35]'
                      } text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40`}
                    />
                    {errors.pincode && <p className="text-xs text-red-500 mt-1 sm:mt-2">{errors.pincode}</p>}
                  </div>
                )}

              {apiError && (
                <div className="bg-red-50 border border-red-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-xs sm:text-sm text-red-700">
                  {apiError}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  'Submit'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
      <FlowFooter />
    </div>
  );
}

export default Employment;
